import { z } from 'zod';

/**
 * DB 워처 계약 (9회차 결정 3).
 *
 * 앱 span은 "쿼리가 몇 ms 걸렸다"까지만 안다. 그 안에서 잠금을 기다렸는지, 풀이 말랐는지,
 * 누가 트랜잭션을 열어 둔 채 쉬고 있는지는 DB 쪽 통계 뷰(`pg_stat_activity`·`pg_locks`)에만 있다.
 * 워처가 1초마다 그것을 읽어 `DbSnapshot`(실시간, 저장 안 함)과 `DbEvent`(사건, 저장)로 나눈다.
 *
 * 두 세계를 잇는 끈은 SQL 주석의 traceparent다(결정 1). 에이전트가 모든 쿼리 끝에
 * `traceparent='00-<traceId>-<spanId>-01'` 주석을 붙이므로 세션 하나가 어느 요청 것인지 알 수 있다.
 */

const nonneg = z.number().nonnegative();
const count = z.number().int().nonnegative();
const traceId = z.string().regex(/^[0-9a-f]{32}$/);
const spanId = z.string().regex(/^[0-9a-f]{16}$/);

/**
 * DB 세션 한 줄. `pg_stat_activity` 한 행에 `pg_blocking_pids()`를 얹은 것이다.
 *
 * 원문 쿼리는 싣지 않는다. 규칙 4에 따라 리터럴을 지우고 앞부분만 남긴 `queryHead`만 나간다.
 */
export const DbSession = z.object({
  pid: z.number().int(),
  /** `active` · `idle` · `idle in transaction` 등 PostgreSQL이 주는 값 그대로 */
  state: z.string(),
  /** `Lock` · `IO` · `Client` 등. 안 기다리는 중이면 없다 */
  waitEventType: z.string().optional(),
  waitEvent: z.string().optional(),
  /** 트랜잭션이 열린 뒤 지난 시간. 트랜잭션 밖이면 없다 */
  xactAgeMs: nonneg.optional(),
  /** 지금 문장이 시작된 뒤 지난 시간 */
  queryAgeMs: nonneg.optional(),
  /** 마지막 상태 변화 뒤 지난 시간. `idle in transaction`이 얼마나 됐는지 볼 때 쓴다 */
  stateAgeMs: nonneg.optional(),
  /** 마스킹하고 자른 문장 앞부분. traceparent 주석은 뗀 뒤다 */
  queryHead: z.string(),
  /** SQL 주석에서 뽑은 값. 주석이 잘렸거나 앱 밖 세션이면 없다 */
  traceId: traceId.optional(),
  spanId: spanId.optional(),
  /** 이 세션을 막고 있는 pid들 */
  blockedBy: z.array(z.number().int()).optional(),
  /** `application_name`. 어느 앱이 붙었는지 구분한다 */
  appName: z.string().optional(),
  /** 접속 계정 */
  userName: z.string().optional(),
  /**
   * `track_activity_query_size`(기본 1024자)에 걸려 문장이 잘렸다.
   * 주석이 끝에 붙으므로 잘리면 traceId 매칭이 안 된다. 매칭률과 함께 진단에 쓴다.
   */
  truncated: z.boolean().optional(),
});
export type DbSession = z.infer<typeof DbSession>;

/**
 * 1초마다 만드는 DB 상태 한 장. **저장하지 않는다**(에이전트 스냅샷과 같은 취급).
 * 링버퍼에 30분치만 두고 SSE로 화면에 밀어 준다.
 */
export const DbSnapshot = z.object({
  ts: z.number().int().nonnegative(),
  /** 워처를 돌리는 관제 서버 이름. 여러 DB를 볼 때 구분용 */
  service: z.string().optional(),
  database: z.string().min(1),
  /** `state = 'active'` 세션 수 */
  active: count,
  /** `idle in transaction`(+ aborted) 세션 수 */
  idleInTx: count,
  /** `wait_event_type`이 있는 세션 수 */
  waiting: count,
  /** 다른 pid에 막힌 세션 수 */
  blocked: count,
  longestQueryMs: nonneg,
  longestXactMs: nonneg,
  /**
   * 앱 세션 중 traceId를 뽑아낸 비율(0~1). 주석 잘림이 늘면 이 값이 떨어진다.
   * 앱 세션이 하나도 없으면 1.
   */
  matchRate: z.number().min(0).max(1),
  /** 문장이 잘려 매칭을 못 한 세션 수 */
  truncated: count,
  /** 오래 돈 순 상위 200행 */
  sessions: z.array(DbSession).max(200),
});
export type DbSnapshot = z.infer<typeof DbSnapshot>;

/**
 * 사건 종류.
 * - `lock_wait` 잠금을 기다림(`wait_event_type = 'Lock'`)
 * - `blocked` 다른 세션에 막힘(`pg_blocking_pids()`가 비지 않음)
 * - `idle_in_transaction` 트랜잭션을 열어 둔 채 쉼
 * - `long_query` 한 문장이 오래 돎
 */
export const DB_EVENT_KINDS = [
  'lock_wait',
  'blocked',
  'idle_in_transaction',
  'long_query',
] as const;
export const DbEventKind = z.enum(DB_EVENT_KINDS);
export type DbEventKind = z.infer<typeof DbEventKind>;

/**
 * DB 사건 한 건. 스냅샷 사이의 차이로 만들고 **해소된 뒤에** 확정한다(그래야 길이를 안다).
 * `hot/dbevent/_db/{YYYY-MM-DD}/`에 요약 파일과 같은 회전 규칙으로 저장한다. 사건에는 서비스가
 * 없어 해시 없는 고정 폴더 `_db`로 간다(23-C회차).
 */
export const DbEvent = z.object({
  /** `{kind}-{pid}-{startedAt}`. 같은 사건이 두 번 저장되지 않게 하는 키다 */
  id: z.string().min(1),
  kind: DbEventKind,
  database: z.string().min(1),
  pid: z.number().int(),
  startedAt: z.number().int().nonnegative(),
  /** 해소 시각. 프로세스가 내려가며 강제로 닫은 것도 채운다 */
  endedAt: z.number().int().nonnegative().optional(),
  durationMs: nonneg.optional(),
  /** 그 세션이 처리하던 요청. 주석이 잘렸으면 없다 */
  traceId: traceId.optional(),
  /** 막은 쪽 */
  blockedByPid: z.number().int().optional(),
  blockedByTraceId: traceId.optional(),
  queryHead: z.string(),
  /** `pg_locks.locktype`. 잠금 대기일 때만 */
  lockType: z.string().optional(),
  /** 잠금 대상 관계 이름 */
  relation: z.string().optional(),
  appName: z.string().optional(),
});
export type DbEvent = z.infer<typeof DbEvent>;

/** 화면 문구. 서버 진단과 콘솔이 같은 이름을 쓴다 */
export const DB_EVENT_LABEL: Record<DbEventKind, string> = {
  lock_wait: '잠금 대기',
  blocked: '다른 세션에 막힘',
  idle_in_transaction: '유휴 트랜잭션',
  long_query: '긴 쿼리',
};
